import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { lastValueFrom, retry, catchError } from 'rxjs';
// import { of } from 'rxjs';
import { SmartCacheService } from './smart-cache.service';
import { BackgroundSyncService } from './background-sync.service';
import {
  Area,
  CentroCosto,
  Confidencialidad,
  Ejecutivo,
  EstadosSolicitud,
  ResponseGerencia,
  Habilidad,
  Jornada,
  MinutosColacion,
  Modalidad,
  Motivos,
  MotivosDescarte,
  Oficina,
  ResponseEjecutivoME,
  ResponseEmpresas,
  // TipoBusqueda,
  // TipoRol,
  TiposProceso,
  Urgencia,
  Zona,
  TipoActividad,
  Bono,
  TipoContrato,
  Comuna,
  Region,
  Genero,
  Banco,
  BancoTipoCuenta,
  EstadoCivil,
  Nacionalidad,
  Parentezco,
  Pais,
  Profile,
  ResponseObtenerParamsInfProv,
} from '../core/interfaces/comunes.interface';
import { UserProfile } from '../core/interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class ComunesService {
  private readonly URL = environment.API_DOMAINS.URL_COMUNES_API;

  private http = inject(HttpClient);
  private smartCache = inject(SmartCacheService);
  private backgroundSync = inject(BackgroundSyncService);

  // Tiempo de expiración del caché en memoria (15 minutos) - para compatibilidad
  private readonly CACHE_EXPIRATION_TIME = 900 * 1000;

  // Caché en memoria para uso como fallback
  private cache: {
    confidencialidad?: { data: Confidencialidad[]; timestamp: number };
    // tipoBusqueda?: { data: TipoBusqueda[]; timestamp: number };
    modalidad?: { data: Modalidad[]; timestamp: number };
    jornada?: { data: Jornada[]; timestamp: number };
    motivos?: { data: Motivos[]; timestamp: number };
    minutosColacion?: { data: MinutosColacion[]; timestamp: number };
    tiposProceso?: { data: TiposProceso[]; timestamp: number };
    centroCosto?: { data: CentroCosto[]; timestamp: number };
    oficina?: { data: Oficina[]; timestamp: number };
    zona?: { data: Zona[]; timestamp: number };
    ejecutivo?: { data: Ejecutivo[]; timestamp: number };
    area?: { data: Area[]; timestamp: number };
    gerencia?: { data: ResponseGerencia[]; timestamp: number };
    habilidades?: { data: Habilidad[]; timestamp: number };
    // tiposRol?: { data: TipoRol[]; timestamp: number };
    estadosSolicitud?: { data: EstadosSolicitud[]; timestamp: number };
    urgencias?: { data: Urgencia[]; timestamp: number };
    motivosDescarte?: { data: MotivosDescarte[]; timestamp: number };
    tiposActividad?: { data: TipoActividad[]; timestamp: number };
    comunas?: { data: Comuna[]; timestamp: number };
    regiones?: { data: Region[]; timestamp: number };
    nacionalidades?: { data: Nacionalidad[]; timestamp: number };
    generos?: { data: Genero[]; timestamp: number };
    bancos?: { data: Banco[]; timestamp: number };
    bancoTiposCuenta?: { data: BancoTipoCuenta[]; timestamp: number };
    estadosCiviles?: { data: EstadoCivil[]; timestamp: number };
    parentezcos?: { data: Parentezco[]; timestamp: number };
    paises?: { data: Pais[]; timestamp: number };
    perfiles?: { data: Profile[]; timestamp: number };
    obtenerParamsInfProv?: {
      data: ResponseObtenerParamsInfProv;
      timestamp: number;
      params?: HttpParams;
    };
  } = {};

  constructor() {
    // Detectar cambios de conectividad
    window.addEventListener('online', () => {
      this.backgroundSync.notifyOnline();
    });
    window.addEventListener('offline', () => {
      this.backgroundSync.notifyOffline();
    });
  }

  private isCacheValid(timestamp?: number): boolean {
    if (!timestamp) return false;
    return Date.now() - timestamp < this.CACHE_EXPIRATION_TIME;
  }

  /**
   * Recupera datos del caché si están vigentes, o los obtiene de la API y los almacena.
   * Usa SmartCacheService para caché persistente e inteligente.
   * Usar solo en endpoints GET sin parámetros dinámicos.
   */
  private async getCached<T>(cacheKey: string, url: string, params?: HttpParams): Promise<T> {
    // Obtener del caché inteligente
    const cachedData = await this.smartCache.get<T>(cacheKey, params);
    if (cachedData) {
      return cachedData;
    }

    try {
      // Si no hay caché, obtener de la API
      const res = await lastValueFrom(
        this.http.get<T>(url, { params }).pipe(
          retry(1),
          catchError((error) => {
            console.error(`Error al obtener ${cacheKey}:`, error);
            throw error;
          }),
        ),
      );

      // Guardar en caché
      await this.smartCache.set<T>(cacheKey, res, params);

      return res;
    } catch (error) {
      // Si falla la red y hay caché antiguo en memoria, usarlo como fallback
      const cache = this.cache as Record<
        string,
        { data: T; timestamp: number; params?: HttpParams } | undefined
      >;
      if (cache[cacheKey]?.data) {
        console.warn(`Usando caché antiguo como fallback para ${cacheKey}`);
        return cache[cacheKey]!.data;
      }
      throw error;
    }
  }

  async limpiarCache(): Promise<void> {
    this.cache = {};
    await this.smartCache.clear();
    console.log('Caché limpiado completamente');
  }

  async obtenerConfidencialidad(): Promise<Confidencialidad[]> {
    const cacheKey = 'obtenerConfidencialidad';

    // Intentar obtener del caché inteligente
    const cached = await this.smartCache.get<Confidencialidad[]>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const res = await lastValueFrom(
        this.http.get<Confidencialidad[]>(`${this.URL}/obtenerConfidencialidad`).pipe(retry(1)),
      );
      const filtered = res.filter((item) => Number(item.confActivo) === 1);

      // Guardar caché filtrado
      await this.smartCache.set<Confidencialidad[]>(cacheKey, filtered);
      this.cache.confidencialidad = { data: filtered, timestamp: Date.now() };

      return filtered;
    } catch (error) {
      // Fallback a caché antiguo
      if (this.cache.confidencialidad?.data) {
        console.warn('Usando caché antiguo de confidencialidad');
        return this.cache.confidencialidad.data;
      }
      throw error;
    }
  }

  // async obtenerTipoBusqueda(): Promise<TipoBusqueda[]> {
  //   return this.getCached<TipoBusqueda[]>('tipoBusqueda', `${this.URL}/obtenerTipoBusqueda`);
  // }

  async obtenerModalidad(): Promise<Modalidad[]> {
    return this.getCached<Modalidad[]>('modalidad', `${this.URL}/obtenerModalidad`);
  }

  async obtenerJornada(): Promise<Jornada[]> {
    return this.getCached<Jornada[]>('jornada', `${this.URL}/obtenerJornada`);
  }

  async obtenerMotivos(): Promise<Motivos[]> {
    return this.getCached<Motivos[]>('motivos', `${this.URL}/obtenerMotivos`);
  }

  async obtenerMinutosColacion(): Promise<MinutosColacion[]> {
    return this.getCached<MinutosColacion[]>(
      'minutosColacion',
      `${this.URL}/obtenerMinutosColacion`,
    );
  }

  async obtenerEmpresas(
    props?: Partial<{ nombre: string; codigo: string; limit: string; page: string }>,
  ): Promise<ResponseEmpresas> {
    let params: HttpParams = new HttpParams();

    if (props?.nombre && props.nombre.length > 0) {
      params = params.set('nombre', props.nombre.trim());
    }
    if (props?.codigo && props.codigo.length > 0) {
      params = params.set('codigo', props.codigo.trim());
    }
    if (props?.limit && props.limit.length > 0) {
      params = params.set('limit', props.limit.trim());
    }
    if (props?.page && props.page.length > 0) {
      params = params.set('page', props.page.trim());
    }

    // Intentar obtener del caché inteligente (memory cache para parámetros dinámicos)
    const cached = await this.smartCache.get<ResponseEmpresas>('obtenerEmpresas', params);
    if (cached) {
      return cached;
    }

    try {
      const result = await lastValueFrom(
        this.http.get<ResponseEmpresas>(`${this.URL}/obtenerEmpresas`, { params }).pipe(retry(1)),
      );

      // Cachear resultado con parámetros
      await this.smartCache.set<ResponseEmpresas>('obtenerEmpresas', result, params);

      return result;
    } catch (error) {
      console.error('Error al obtener empresas:', error);
      throw error;
    }
  }

  async obtenerTiposProceso(): Promise<TiposProceso[]> {
    return this.getCached<TiposProceso[]>('tiposProceso', `${this.URL}/obtenerTiposProceso`);
  }

  async obtenerCentroCosto(): Promise<CentroCosto[]> {
    return this.getCached<CentroCosto[]>('centroCosto', `${this.URL}/obtenerCentroCosto`);
  }

  async obtenerPaises(): Promise<Pais[]> {
    return this.getCached<Pais[]>('paises', `${this.URL}/obtenerPaises`);
  }

  async obtenerOficina(): Promise<Oficina[]> {
    return this.getCached<Oficina[]>('oficina', `${this.URL}/obtenerOficina`);
  }

  async obtenerZona(): Promise<Zona[]> {
    return this.getCached<Zona[]>('zona', `${this.URL}/obtenerZona`);
  }

  async obtenerEjecutivo(): Promise<Ejecutivo[]> {
    return this.getCached<Ejecutivo[]>('ejecutivo', `${this.URL}/obtenerEjecutivo`);
  }

  /**
   * Obtiene los ejecutivos por RUT. Usa caché en memoria porque el resultado varía por parámetro.
   */
  async obtenerEjecutivosRut(rut: string): Promise<ResponseEjecutivoME[]> {
    const extractedRut = rut.split('-')?.[0] ? rut.split('-')[0].trim() : rut.trim();
    const params = new HttpParams().set('rut', extractedRut);

    // Intentar obtener del caché (memory cache)
    const cached = await this.smartCache.get<ResponseEjecutivoME[]>('obtenerEjecutivosRut', params);
    if (cached) {
      return cached;
    }

    try {
      const result = await lastValueFrom(
        this.http
          .get<ResponseEjecutivoME[]>(`${this.URL}/obtenerEjecutivosRut/${extractedRut}`)
          .pipe(retry(1)),
      );

      // Cachear resultado
      await this.smartCache.set<ResponseEjecutivoME[]>('obtenerEjecutivosRut', result, params);

      return result;
    } catch (error) {
      console.error('Error al obtener ejecutivos por RUT:', error);
      throw error;
    }
  }

  async obtenerArea(): Promise<Area[]> {
    return this.getCached<Area[]>('area', `${this.URL}/obtenerArea`);
  }

  async obtenerGerencia(): Promise<ResponseGerencia[]> {
    return this.getCached<ResponseGerencia[]>('gerencia', `${this.URL}/obtenerGerencia`);
  }

  async obtenerHabilidades(): Promise<Habilidad[]> {
    return this.getCached<Habilidad[]>('habilidades', `${this.URL}/obtenerHabilidades`);
  }

  // async obtenerTiposRol(): Promise<TipoRol[]> {
  //   return this.getCached<TipoRol[]>('tiposRol', `${this.URL}/obtenerTiposRol`);
  // }

  async obtenerEstadosSolicitud(
    t?: 'originals' | 'ingreso-candidato',
  ): Promise<EstadosSolicitud[]> {
    let httpParams: HttpParams = new HttpParams();
    if (t) {
      httpParams = httpParams.set('t', t);
    }

    // Intentar obtener del caché (memory cache)
    const cached = await this.smartCache.get<EstadosSolicitud[]>(
      'obtenerEstadosSolicitud',
      httpParams,
    );
    if (cached) {
      return cached;
    }

    try {
      const result = await lastValueFrom(
        this.http
          .get<EstadosSolicitud[]>(`${this.URL}/obtenerEstadosSolicitud`, {
            params: httpParams,
          })
          .pipe(retry(1)),
      );

      // Cachear resultado
      await this.smartCache.set<EstadosSolicitud[]>('obtenerEstadosSolicitud', result, httpParams);

      return result;
    } catch (error) {
      console.error('Error al obtener estados de solicitud:', error);
      throw error;
    }
  }

  async obtenerUrgencias(): Promise<Urgencia[]> {
    return this.getCached<Urgencia[]>('urgencias', `${this.URL}/obtenerUrgencias`);
  }

  async obtenerTiposActividad(): Promise<TipoActividad[]> {
    return this.getCached<TipoActividad[]>('tiposActividad', `${this.URL}/obtenerTiposActividad`);
  }

  async obtenerMotivosDescarte(): Promise<MotivosDescarte[]> {
    return this.getCached<MotivosDescarte[]>(
      'obtenerMotivosDescarte',
      `${this.URL}/obtenerMotivosDescarte`,
    );
  }

  async obtenerEjecutivosME(): Promise<ResponseEjecutivoME[]> {
    return this.getCached<ResponseEjecutivoME[]>(
      'obtenerEjecutivos',
      `${this.URL}/obtenerEjecutivos`,
    );
  }

  async obtenerBonos(): Promise<Bono[]> {
    return this.getCached<Bono[]>('obtenerBonos', `${this.URL}/obtenerBonos`);
  }

  async obtenerTiposContrato(): Promise<TipoContrato[]> {
    return this.getCached<TipoContrato[]>(
      'obtenerTiposContrato',
      `${this.URL}/obtenerTiposContrato`,
    );
  }

  async getSignedUrl(props: {
    key?: string;
    expiresInSeconds?: string;
  }): Promise<{ message: string }> {
    let httpParams: HttpParams = new HttpParams();

    Object.keys(props).forEach((key) => {
      const value = props[key as keyof typeof props];
      if (
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !['null', 'undefined'].includes(value)
      ) {
        httpParams = httpParams.set(key, value);
      }
    });

    // S3 URLs NUNCA se cachean - network-only
    try {
      return await lastValueFrom(
        this.http
          .get<{ message: string }>(`${this.URL}/getSignedUrl`, { params: httpParams })
          .pipe(retry(1)),
      );
    } catch (error) {
      console.error('Error al obtener URL firmada de S3:', error);
      throw error;
    }
  }

  async obtenerComunas(): Promise<Comuna[]> {
    return this.getCached<Comuna[]>('comunas', `${this.URL}/obtenerComunas`);
  }

  async obtenerRegiones(): Promise<Region[]> {
    return this.getCached<Region[]>('regiones', `${this.URL}/obtenerRegiones`);
  }

  async obtenerNacionalidades(): Promise<Nacionalidad[]> {
    return this.getCached<Nacionalidad[]>('nacionalidades', `${this.URL}/obtenerNacionalidades`);
  }

  async obtenerGeneros(): Promise<Genero[]> {
    return this.getCached<Genero[]>('generos', `${this.URL}/obtenerGeneros`);
  }

  async obtenerBancos(): Promise<Banco[]> {
    return this.getCached<Banco[]>('bancos', `${this.URL}/obtenerBancos`);
  }

  async obtenerBancoTiposCuenta(): Promise<BancoTipoCuenta[]> {
    return this.getCached<BancoTipoCuenta[]>(
      'bancoTiposCuenta',
      `${this.URL}/obtenerBancoTiposCuenta`,
    );
  }

  async obtenerEstadosCiviles(): Promise<EstadoCivil[]> {
    return this.getCached<EstadoCivil[]>('estadosCiviles', `${this.URL}/obtenerEstadosCiviles`);
  }

  async obtenerParentezcos(): Promise<Parentezco[]> {
    return this.getCached<Parentezco[]>('parentezcos', `${this.URL}/obtenerParentezcos`);
  }

  async obtenerPerfiles(): Promise<Profile[]> {
    try {
      return await this.getCached<Profile[]>('perfiles', `${this.URL}/obtenerPerfiles`);
    } catch (error) {
      console.error('Error al obtener perfiles:', error);
      return [];
    }
  }

  async obtenerParamsInfProv(
    params: 'fonasaIsapre' | 'fondoCotizacion' | 'regimenProvisional' | 'todos',
  ): Promise<ResponseObtenerParamsInfProv> {
    try {
      const httpParams = new HttpParams().set('q', params);

      // Intentar obtener del caché (memory cache para parámetros dinámicos)
      const cached = await this.smartCache.get<ResponseObtenerParamsInfProv>(
        'obtenerParamsInfProv',
        httpParams,
      );
      if (cached) {
        return cached;
      }

      const result = await lastValueFrom(
        this.http
          .get<ResponseObtenerParamsInfProv>(`${this.URL}/obtenerParamsInfProv`, {
            params: httpParams,
          })
          .pipe(retry(1)),
      );

      // Cachear resultado
      await this.smartCache.set<ResponseObtenerParamsInfProv>(
        'obtenerParamsInfProv',
        result,
        httpParams,
      );

      return result;
    } catch (error) {
      console.error('Error al obtener parámetros de información previsional:', error);
      return {
        regimenProvisional: undefined,
        fondoCotizacion: undefined,
        fonasaIsapre: undefined,
      };
    }
  }

  /**
   * Precarga en segundo plano los datos estáticos de comunes si aún no están en caché.
   * Fire-and-forget: no bloquea la UI.
   */
  prefetchStaticData(): void {
    const calls = [
      () => this.obtenerConfidencialidad(),
      // () => this.obtenerTipoBusqueda(),
      () => this.obtenerModalidad(),
      () => this.obtenerJornada(),
      () => this.obtenerMotivos(),
      () => this.obtenerMinutosColacion(),
      () => this.obtenerTiposProceso(),
      () => this.obtenerCentroCosto(),
      () => this.obtenerPaises(),
      () => this.obtenerOficina(),
      () => this.obtenerZona(),
      () => this.obtenerEjecutivo(),
      () => this.obtenerArea(),
      () => this.obtenerGerencia(),
      () => this.obtenerHabilidades(),
      // () => this.obtenerTiposRol(),
      () => this.obtenerUrgencias(),
      () => this.obtenerTiposActividad(),
      () => this.obtenerMotivosDescarte(),
      () => this.obtenerEjecutivosME(),
      () => this.obtenerBonos(),
      () => this.obtenerTiposContrato(),
      () => this.obtenerComunas(),
      () => this.obtenerRegiones(),
      () => this.obtenerNacionalidades(),
      () => this.obtenerGeneros(),
      () => this.obtenerBancos(),
      () => this.obtenerBancoTiposCuenta(),
      () => this.obtenerEstadosCiviles(),
      () => this.obtenerParentezcos(),
      () => this.obtenerPerfiles(),
    ];

    calls.forEach((call) => {
      call().catch(() => {
        // Silenciar errores en prefetch de fondo
      });
    });
  }
}
